'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import cn from 'classnames';
import { type Icon, type IconProps } from '@phosphor-icons/react';

import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import TextButton from '_components/UI/TextButton';
import IconButton, { type IconButtonProps } from '_components/UI/IconButton';
import LoadingSpinner from '@components/ui/LoadingSpinner';
// Relative, not `@components/...` — the alias resolves before Jest's
// blanket `.svg` mock rule in `moduleNameMapper`, so an aliased import here
// hits the real file (and its non-JS content) under test instead of the mock.
import CartAddSvg from '../../../components/ui/Icons/cart-add.svg';

import styles from './QuickAddTrigger.module.scss';

/**
 * Adapts the shared `LoadingSpinner` (a CSS spinner, not an SVG icon) to
 * Phosphor's `Icon` shape so it can stand in for `IconButton`'s `icon` prop
 * while an add is in flight (`pending` below). `currentColor` picks up
 * whatever the button face's own icon colour already is — white on the
 * primary/inline face — instead of needing its own colour prop threaded in.
 */
const SpinnerIcon: Icon = forwardRef<SVGSVGElement, IconProps>(
  (_props, _ref) => <LoadingSpinner colour="currentColor" />
);
SpinnerIcon.displayName = 'SpinnerIcon';

/**
 * Adapts `components/ui/Icons/cart-add.svg` — Figma's "ShoppingCartAdd"
 * glyph (360:8984, the same cart + plus as 801:82864's combined layers) —
 * to Phosphor's `Icon` shape so it drops straight into `IconButton`'s `icon`
 * prop (and, via `className`, `TextButton`'s `leadingIcon`). Every Quick Add
 * face uses it: the PLP hover CTA, the PLP/hotspot icon buttons and the Why
 * Choose chip, at 16-32px.
 *
 * The SVG keeps its strokes (`currentColor`, so each face's existing
 * `svg { color }` rule still colours it) with
 * `vector-effect="non-scaling-stroke"`: the outline scales with `size` but
 * the line is set in pixels per size by `cartAddStrokeWidth`. A flattened,
 * filled path scales its line weight with the icon, so one tuned for
 * 16-24px read as heavy at 32px. The stroke attributes sit on the root
 * `<svg>` so the inline `strokeWidth` below cascades to every path.
 *
 * `viewBox` is passed explicitly because SVGO's `removeViewBox` (on by
 * default in the app's `@svgr/webpack` pipeline and Storybook's) strips one
 * that matches the source's `width`/`height`; without it a different `size`
 * would clip the 32×32 artwork instead of scaling it. `className` is
 * forwarded since `TextButton` relies on it for `.leadingIcon` layout.
 */
/**
 * Line weight in px for a given icon size. Up to 24px it reproduces the
 * weight the previous flattened icon shipped with (Figma's 1.25 units, bumped
 * ~40% for legibility after QA: 1.75px at 24px, scaling down with the icon),
 * so the PLP and hotspot faces look unchanged. Above that it's Figma
 * 360:8984's 1.5px for the 32px Why Choose chip.
 */
const cartAddStrokeWidth = (size: number) =>
  size > 24 ? 1.5 : (size * 1.75) / 24;

export const CartAddIcon: Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ size, className }, _ref) => (
    <CartAddSvg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={className}
      style={{ strokeWidth: cartAddStrokeWidth(Number(size)) }}
    />
  )
);
CartAddIcon.displayName = 'CartAddIcon';

export interface QuickAddTriggerProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Visible CTA label (desktop face only). */
  label: string;
  /** Compact always-visible icon face (mobile); default is the hover CTA face. */
  isMobileLayout?: boolean;
  /**
   * Icon face styling, only meaningful when `isMobileLayout` is true:
   * - `'overlay'` (default): white/outline, pinned to the bottom-right
   *   corner of an image-sized host (PLP grid card).
   * - `'inline'`: primary/filled, sized to fill its host rather than
   *   corner-pinned — for a host that IS the button's own slot, not an
   *   image (`ProductTile`'s hotspot card, Figma node 427:7198).
   * - `'withImage'`: the `'inline'` face at Figma's "Add to Cart Button,
   *   State=With Image" size (360:8984) — a 48px square with a 32px glyph at
   *   every breakpoint, beside a product thumbnail (`WhyChoose`'s chip).
   */
  iconVariant?: 'overlay' | 'inline' | 'withImage';
  /**
   * Swaps the cart icon for a spinner and stops the button from reopening
   * the popover — `ImageHotspot`'s compact/stacked Quick Add closes its
   * size-selector card entirely while an add is in flight (Figma 867:163554)
   * rather than showing a spinner inside it, so the button itself is what
   * tells the shopper something's happening. Not native `disabled`: that
   * would also fade the button (`IconButton`'s `.disabled`), which reads as
   * "can't be used" rather than "in progress" — the spinner already says
   * that, so only the click handler is suppressed and `aria-disabled` is set
   * instead, keeping the button's normal appearance and focusability.
   */
  pending?: boolean;
  'aria-label': string;
}

/**
 * Quick Add trigger rendered over the PLP card image. Desktop: white
 * `TextButton` CTA (Figma "Text Button") revealed on card hover/focus.
 * Mobile: always-visible `IconButton` in the image's bottom-right corner.
 * Popover props (onClick, aria-*, data-popup-open, ref) pass through, so it
 * can be used with Base UI `Popover.Trigger`'s `render` prop.
 */
const QuickAddTrigger = forwardRef<HTMLButtonElement, QuickAddTriggerProps>(
  (
    {
      label,
      isMobileLayout = false,
      iconVariant = 'overlay',
      pending = false,
      className,
      'aria-label': ariaLabel,
      onClick,
      ...rest
    },
    ref
  ) => {
    // Only the `inline` face (ImageHotspot's `MiniProductCard` cart action,
    // Figma 419:6774) has its own Mobile/Desktop size variants — 44px/20px
    // icon below `tablet-xl` (988px, matching `MiniProductCard`'s own image
    // breakpoint), 56px/24px icon above it. The `overlay` face (PLP grid,
    // `isMobileLayout` already true only on real mobile) has no such split,
    // so this is read but unused there.
    const isCompactInline = useBreakpoint(Breakpoints.tabletXl);

    if (isMobileLayout) {
      const withImage = iconVariant === 'withImage';
      const inline = iconVariant === 'inline' || withImage;
      const inlineSize = isCompactInline ? 'md' : 'xl';
      const inlineIconSize = isCompactInline ? 20 : 24;
      return (
        <IconButton
          {...rest}
          ref={ref}
          // IconButton's handler is typed for button|anchor; ours is button-only.
          // Suppressed entirely while pending — see the prop's own doc for why.
          onClick={
            pending ? undefined : (onClick as IconButtonProps['onClick'])
          }
          // Figma 801:82864's own cart+plus glyph (see `CartAddIcon` above)
          // — a single icon, not a cart icon with a badge overlaid on top.
          icon={pending ? SpinnerIcon : CartAddIcon}
          type={inline ? 'primary' : 'outline'}
          size={withImage || !inline ? 'sm' : inlineSize}
          shape="square"
          iconSize={withImage ? 32 : inline ? inlineIconSize : 16}
          aria-label={ariaLabel}
          aria-disabled={pending || undefined}
          className={cn(
            inline ? styles.iconButtonInline : styles.iconButton,
            className
          )}
        />
      );
    }

    return (
      <TextButton
        {...rest}
        ref={ref}
        onClick={onClick}
        type="white"
        size="small"
        label={label}
        leadingIcon={CartAddIcon}
        leadingIconSize={24}
        aria-label={ariaLabel}
        className={cn(styles.cta, className)}
      />
    );
  }
);

QuickAddTrigger.displayName = 'QuickAddTrigger';

export default QuickAddTrigger;
